import { ReactNode, useState } from 'react';
import { DialogTrigger, Popover } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import { UserAvatar } from '@/components/UserAvatar/UserAvatar';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import InputSearch from '@/design_system/InputSearch';
import InlineText from '@/design_system/Typography/InlineText';
import IconCheck from '@/icons/Check.svg';
import { UserFromGetMany } from '@/models/user';
import { createBEMClasses } from '@/utils/classname';

import './UserPicker.css';

interface UserPickerProps {
  size: 'xx-small' | 'x-small' | 'small' | 'medium' | 'large';
  ariaLabel: string;
  users: UserFromGetMany[];
  isLoadingUsers?: boolean;
  selectedUser: { id: string; name: string } | null;
  onSelectUser: (user: UserFromGetMany | null) => void;
  search: string;
  onSearchChange: (searchTerm: string) => void;
  tooltip?: ReactNode;
}

const { block, element } = createBEMClasses('user-picker');

export const UserPicker = ({
  size,
  ariaLabel,
  users,
  isLoadingUsers,
  selectedUser,
  onSelectUser,
  search,
  onSearchChange,
  tooltip,
}: UserPickerProps) => {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);

  const label = t({
    id: 'user-picker.search.label',
    message: 'Search users...',
  });

  const avatarTooltip =
    tooltip ?? (selectedUser ? <p className="sentry-mask">{selectedUser.name}</p> : null);

  return (
    <DialogTrigger onOpenChange={setOpen} isOpen={open} aria-label={ariaLabel}>
      <UserAvatar
        ariaLabel={ariaLabel}
        size={size}
        user={selectedUser}
        tooltip={avatarTooltip}
        isButton
      />
      <Popover placement="bottom end" offset={4}>
        <Box padding="0" className={block()}>
          <InputSearch
            className={element('search')}
            ariaLabel={label}
            placeholder={label}
            value={search}
            onChange={onSearchChange}
            isLoading={isLoadingUsers}
          />
          <div className={element('list')}>
            {users.map((user) => (
              <Button
                className={element('item')}
                key={user.id}
                variant="style-less"
                onPress={() => {
                  onSelectUser(selectedUser?.id === user.id ? null : user);
                  setOpen(false);
                  onSearchChange('');
                }}
              >
                <UserAvatar user={user} size="small" />
                <InlineText ellipsis className="sentry-mask" flex={1} title={user.name}>
                  {user.name}
                </InlineText>
                {selectedUser?.id === user.id && <IconCheck style={{ fontSize: '20px' }} />}
              </Button>
            ))}
            {users.length === 0 && (
              <InlineText size="200" className={element('no-items')}>
                <Trans id="user-picker.search.no-users">
                  No results. Please try using different keywords.
                </Trans>
              </InlineText>
            )}
          </div>
        </Box>
      </Popover>
    </DialogTrigger>
  );
};
